import { Check, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Fragment } from 'react';
import Spinner from '@/components/spinner/Spinner';

interface StepProps {
	title: string;
	name: string;
	isCompleted?: boolean;
	isActive?: boolean;
}

const Step = ({ title, name, isCompleted, isActive }: StepProps) => {
	return (
		<div className="flex items-center">
			<div className="relative flex items-center justify-center">
				<div
					className={cn(
						'w-8 h-8 rounded-full border-2 flex items-center justify-center',
						isCompleted
							? 'border-primary bg-primary text-primary-foreground'
							: isActive
								? 'border-primary'
								: 'border-muted'
					)}
				>
					{isCompleted ? <Check className="w-4 h-4" /> : <span className="text-sm font-medium">{title}</span>}
				</div>
			</div>
			<div className="ml-4">
				<p className="text-sm text-muted-foreground">{name}</p>
			</div>
		</div>
	);
};

interface StepperProps {
	steps: Array<{ name: string }>;
	submitBtnLabel?: string;
	currentStep: number;
	onStepChange: (step: number) => void;
	isLoading?: boolean;
	onSubmit: () => void;
}

export const Stepper = ({
	steps,
	currentStep,
	onStepChange,
	isLoading,
	onSubmit,
	submitBtnLabel = 'Deploy'
}: StepperProps) => {
	return (
		<div className="w-full max-w-4xl mx-auto">
			<div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
				{steps.map((step, index) => (
					<Fragment key={step.name}>
						<Step
							title={(index + 1).toString()}
							name={step.name}
							isCompleted={index < currentStep}
							isActive={index === currentStep}
						/>
						{index < steps.length - 1 && <ChevronRight className="hidden md:block text-muted-foreground" />}
					</Fragment>
				))}
			</div>
			<div className="flex justify-between">
				<Button variant="outline" onClick={() => onStepChange(currentStep - 1)} disabled={currentStep === 0}>
					Previous
				</Button>
				<Button
					onClick={() => {
						if (currentStep < steps.length - 1) {
							return onStepChange(currentStep + 1);
						}
						onSubmit();
					}}
					disabled={isLoading}
				>
					{currentStep === steps.length - 1 ? submitBtnLabel : 'Next'}
					{isLoading && <Spinner className="ml-2" />}
				</Button>
			</div>
		</div>
	);
};
